import { useState } from 'react';
import { useProvince } from '../contexts/ProvinceContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/useToast';
import { useDiplomacy } from '../hooks/useDiplomacy';
import { useAppShellPlatform } from '../hooks/useAppShellPlatform';
import { invalidateKingdomRelationships } from '../hooks/useKingdomRelationships';
import { MobileRelationsShell } from '../components/relations/MobileRelationsShell';
import { DesktopRelationsShell } from '../components/relations/DesktopRelationsShell';
import { ConfirmModal } from '../components/ConfirmModal';
import { COLORS } from '../constants/theme';
import { formatGameDate } from '../utils/gameTime';
import {
  CONFIRM_COPY,
  HOSTILITY_UNFRIENDLY_THRESHOLD,
  HOSTILITY_HOSTILE_THRESHOLD,
  HOSTILITY_CAP,
} from '../components/relations/relationsPresentation';
import {
  declareWar,
  proposePeace,
  withdraw,
  cancelCeasefire,
} from '../api/diplomacy';
import type { ActiveWarDto, KingdomDiplomacyResponse } from '../types/diplomacy';

export type RelationsTab = 'overview' | 'war' | 'history';

/** SF-158: replaces the single `isActing: boolean` — a shared boolean can't tell a screen reader
 *  (or a test) which of up to four stacked buttons is in flight, same reasoning as SF-153's
 *  DragonActionKey. */
export type RelationsActionKey = 'declareWar' | 'proposePeace' | 'withdraw' | 'cancelCeasefire';

/** The three actions that go through the single page-level <ConfirmModal> — Propose Peace stays
 *  ungated (Phase 3 Decision 7), so it's excluded here, making "Propose Peace has a confirm state"
 *  a compile error rather than a runtime invariant. */
type ConfirmableRelationsAction = Exclude<RelationsActionKey, 'proposePeace'>;

/**
 * SF-158: named, fully-typed props shared by MobileRelationsShell/DesktopRelationsShell — the
 * DragonsShellProps/ThieveryShellProps precedent. State stays hoisted here (not per-shell) so a
 * breakpoint flip mid-declare-war-form-fill doesn't lose in-progress input, the active tab, an
 * in-flight action, or an open confirm modal.
 */
export interface RelationsShellProps {
  data: KingdomDiplomacyResponse | null;
  isLoading: boolean;
  error: string | null;
  activeTab: RelationsTab;
  setActiveTab: (tab: RelationsTab) => void;
  busyAction: RelationsActionKey | null;
  isMonarch: boolean;
  activeWar: ActiveWarDto | null;
  declareKingdomNumber: string;
  setDeclareKingdomNumber: (value: string) => void;
  declareIslandNumber: string;
  setDeclareIslandNumber: (value: string) => void;
  declareBlockedReason: string | null;
  warStartedText: string | null;
  mutationVersion: number;
  handleDeclareWarRequest: () => void;
  handleProposePeace: () => void;
  handleWithdrawRequest: () => void;
  handleCancelCeasefireRequest: () => void;
}

/**
 * Non-blocking, client-side-only preview of DeclareWarAction's three hostility-derived
 * prerequisites (ours >= 5, theirs >= 15, not-more-aggressive unless both at the 200 cap) — NW
 * range, war-cooldown, and already-at-war are real server-side prerequisites this does NOT check
 * (no NW/cooldown data is in useDiplomacy()'s payload); the server response remains authoritative
 * on submit. `null` when the typed target doesn't match a known relationships row, or when it
 * matches and every prerequisite passes.
 */
function computeDeclareBlockedReason(
  data: KingdomDiplomacyResponse | null,
  kingdomNumber: string,
  islandNumber: string
): string | null {
  if (!data || !kingdomNumber.trim() || !islandNumber.trim()) return null;

  const targetId = `${kingdomNumber.trim()}:${islandNumber.trim()}`;
  const row = data.relationships.find((r) => r.kingdomId === targetId);
  if (!row) return null;

  const { ourHostilityPoints: ours, theirHostilityPoints: theirs } = row;

  if (ours < HOSTILITY_UNFRIENDLY_THRESHOLD) {
    return `Your hostility toward this kingdom (${ours}) is below the ${HOSTILITY_UNFRIENDLY_THRESHOLD} required to declare war.`;
  }
  if (theirs < HOSTILITY_HOSTILE_THRESHOLD) {
    return `This kingdom's hostility toward you (${theirs}) is below the ${HOSTILITY_HOSTILE_THRESHOLD} required for them to be a valid target.`;
  }
  const bothAtCap = ours === HOSTILITY_CAP && theirs === HOSTILITY_CAP;
  if (!bothAtCap && ours > theirs) {
    return 'Your kingdom is more aggressive than theirs toward each other — the less-aggressive kingdom must be the one to declare.';
  }
  return null;
}

export function Relations() {
  const { province } = useProvince();
  const { worldId, provinceId } = useAuth();
  const { addToast } = useToast();
  const platform = useAppShellPlatform();

  const [activeTab, setActiveTab] = useState<RelationsTab>('overview');
  const [declareKingdomNumber, setDeclareKingdomNumber] = useState('');
  const [declareIslandNumber, setDeclareIslandNumber] = useState('');
  const [busyAction, setBusyAction] = useState<RelationsActionKey | null>(null);
  const [mutationVersion, setMutationVersion] = useState(0);
  const [pendingConfirm, setPendingConfirm] = useState<ConfirmableRelationsAction | null>(null);

  const kingdomId = province?.metadata?.location
    ? `${province.metadata.location.kingdom}:${province.metadata.location.island}`
    : null;

  const { data, isLoading, error, refresh } = useDiplomacy(kingdomId);

  const isMonarch = province?.metadata?.isMonarch ?? false;
  const activeWar = data?.activeWar ?? null;
  const warStartedText = activeWar ? formatGameDate(activeWar.warStartTick) : null;
  const declareBlockedReason = computeDeclareBlockedReason(data, declareKingdomNumber, declareIslandNumber);

  const handleAction = async (
    key: RelationsActionKey,
    action: () => Promise<unknown>,
    successMsg: string
  ) => {
    if (!worldId || !provinceId) return;
    if (busyAction !== null) return;
    setBusyAction(key);
    try {
      await action();
      addToast(successMsg, 'success');
      setMutationVersion((v) => v + 1);
      const refreshed = await refresh();
      if (!refreshed) {
        addToast('Action completed, but the latest status could not be loaded — try refreshing.', 'failure');
      }
      if (worldId && kingdomId) invalidateKingdomRelationships(worldId, kingdomId);
    } catch (err: unknown) {
      addToast(err instanceof Error ? err.message : 'Action failed', 'failure');
    } finally {
      setBusyAction(null);
    }
  };

  const handleDeclareWarRequest = () => {
    const k = declareKingdomNumber.trim();
    const i = declareIslandNumber.trim();
    if (!k || !i || Number.isNaN(Number(k)) || Number.isNaN(Number(i))) return;
    setPendingConfirm('declareWar');
  };

  const handleWithdrawRequest = () => setPendingConfirm('withdraw');
  const handleCancelCeasefireRequest = () => setPendingConfirm('cancelCeasefire');

  const handleProposePeace = () =>
    handleAction('proposePeace', () => proposePeace(worldId!, provinceId!), 'Peace proposal submitted');

  const handleConfirm = () => {
    const action = pendingConfirm;
    setPendingConfirm(null);
    if (action === 'declareWar') {
      const targetKingdomId = `${declareKingdomNumber.trim()}:${declareIslandNumber.trim()}`;
      handleAction('declareWar', () => declareWar(worldId!, provinceId!, targetKingdomId), 'War declared!');
    } else if (action === 'withdraw') {
      handleAction('withdraw', () => withdraw(worldId!, provinceId!), 'Withdrawn from war');
    } else if (action === 'cancelCeasefire') {
      handleAction('cancelCeasefire', () => cancelCeasefire(worldId!, provinceId!), 'Ceasefire cancelled');
    }
  };

  const handleCancel = () => setPendingConfirm(null);

  const confirmBody = () => {
    if (pendingConfirm === 'declareWar') {
      return (
        <>
          <p>
            Declare war on {declareKingdomNumber}:{declareIslandNumber}? This commits your kingdom to an
            extended, costly conflict.
          </p>
          {declareBlockedReason && (
            <p style={{ color: COLORS.warning, fontSize: 13 }}>{declareBlockedReason}</p>
          )}
        </>
      );
    }
    if (pendingConfirm === 'withdraw') {
      return <p>Withdraw from the current war?</p>;
    }
    if (pendingConfirm === 'cancelCeasefire') {
      return <p>Cancel the current ceasefire and resume hostilities?</p>;
    }
    return null;
  };

  const shellProps: RelationsShellProps = {
    data,
    isLoading,
    error,
    activeTab,
    setActiveTab,
    busyAction,
    isMonarch,
    activeWar,
    declareKingdomNumber,
    setDeclareKingdomNumber,
    declareIslandNumber,
    setDeclareIslandNumber,
    declareBlockedReason,
    warStartedText,
    mutationVersion,
    handleDeclareWarRequest,
    handleProposePeace,
    handleWithdrawRequest,
    handleCancelCeasefireRequest,
  };

  return (
    <>
      {platform === 'mobile'
        ? <MobileRelationsShell {...shellProps} />
        : <DesktopRelationsShell {...shellProps} />}

      <ConfirmModal
        isOpen={pendingConfirm !== null}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        title={pendingConfirm ? CONFIRM_COPY[pendingConfirm].title : ''}
        confirmText={pendingConfirm ? CONFIRM_COPY[pendingConfirm].confirmText : undefined}
        cancelText={pendingConfirm ? CONFIRM_COPY[pendingConfirm].cancelText : undefined}
      >
        {confirmBody()}
      </ConfirmModal>
    </>
  );
}
